import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { modelsData } from '../data/modelsData';
import '../ModelDetail.css';
// Импортируем компоненты верстки
import MotorradHeader from '../components/MotorradHeader';
import MotorradFooter from '../components/MotorradFooter';
import MotorradTestDrive from './MotorradTestDrive';
// Используем наш единый настроенный API-сервис
import { sendFrontAction } from '../services/api';

const ModelDetail = () => {
    const { id } = useParams();

    const [model, setModel] = useState(modelsData[id] || null);
    const [availableColors, setAvailableColors] = useState([]);
    const [activeColor, setActiveColor] = useState(null);
    const [loading, setLoading] = useState(!modelsData[id]);

    const specificationsRef = useRef(null);
    const videoRef = useRef(null);

    // Цвета берутся из данных модели, пришедших с бэкенда (model.colors из БД).
    // Реагирует на любое обновление model, включая ответ fetchMotoData.
    useEffect(() => {
        if (model?.colors && model.colors.length > 0) {
            setAvailableColors(model.colors);
            setActiveColor(model.colors[0]);
        } else {
            // Нет цветов в БД для этой модели — фолбэк на главное фото,
            // чтобы секция не показывала цвета/картинку предыдущей модели
            const fallbackImg = model?.mainImage || model?.main_image;
            const fallback = fallbackImg
                ? [{
                    id: 'default',
                    name: model?.name || '',
                    hex: '#cccccc',
                    img: fallbackImg,
                  }]
                : [];
            setAvailableColors(fallback);
            setActiveColor(fallback[0] || null);
        }
    }, [model]);

    // Хук для гарантированного программного запуска видео при обновлении данных
    useEffect(() => {
        if (videoRef.current && model?.heroVideo) {
            videoRef.current.defaultMuted = true;
            videoRef.current.muted = true; // Важно для iOS Safari
            videoRef.current.load();

            const playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.log("Автозапуск заблокирован, видео ожидает действия пользователя:", error);
                });
            }
        }
    }, [model?.heroVideo]);

    useEffect(() => {
        window.scrollTo(0, 0);

        // Сбрасываем цвета сразу при смене модели, чтобы не мелькали
        // цвета предыдущего мотоцикла до прихода ответа от API
        setAvailableColors([]);
        setActiveColor(null);

        const fetchMotoData = async () => {
            try {
                const response = await sendFrontAction('motorrad', { slug: id });

                // Проверяем структуру ответа (данные приходят в поле data)
                const motoData = response?.data ? response.data : response;
                if (motoData) {
                    setModel(prevModel => ({
                        ...prevModel,
                        ...motoData, // Сюда прилетят основные данные (имя, видео, характеристики, цвета)
                    }));
                }
            } catch (error) {
                console.error("Ошибка запроса мото-данных через диспетчер:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchMotoData();

        const blocks = document.querySelectorAll('.specs-category-block');
        const observerOptions = { root: null, rootMargin: '0px 0px -100px 0px', threshold: 0.15 };
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) entry.target.classList.add('visible');
            });
        }, observerOptions);
        blocks.forEach(block => observer.observe(block));
        return () => {
            blocks.forEach(block => observer.unobserve(block));
        };
    }, [id]);

    if (loading) return <div className="not-found">Загрузка данных о мотоцикле...</div>;
    if (!model) return <div className="not-found">Модель мотоцикла не найдена в системе</div>;

    return (
        <div className="model-detail-page">

           {/* 1. HERO SECTION */}
            <section className="model-hero">
                {model && model.heroVideo ? (
                    <video
                        ref={videoRef}
                        className="hero-bg hero-video"
                        autoPlay
                        loop
                        muted
                        playsInline
                        key={model.heroVideo}
                        poster={model.mainImage || model.main_image}
                    >
                        <source src={model.heroVideo} type={/\.webm/i.test(model.heroVideo) ? "video/webm" : "video/mp4"} />
                        Ваш браузер не поддерживает видео.
                    </video>
                ) : null}
                {!model.heroVideo && (
                    <img
                        src={model.mainImage || model.main_image}
                        alt={model.name || "BMW Motorrad"}
                        className="hero-bg hero-image"
                    />
                )}
            </section>

            {/* 3. COLOR SELECTOR */}
            {availableColors && availableColors.length > 0 && (
                <section className="color-selector-section">
                    <div className="container">

                        {/* СТИЛЬНЫЙ ЗАГОЛОВОК МОДЕЛИ ВНУТРИ СЕКЦИИ ЦВЕТОВ */}
                        <div className="color-section-header">
                            <h2 className="model-dynamic-title">{model.name}</h2>
                            <p className="model-dynamic-subtitle">{model.subTitle || model.subtitle}</p>
                        </div>
                        <div className="bike-display">
                            {activeColor?.img && (
                                <img src={activeColor.img} alt={`BMW ${model.name} ${activeColor.name}`} className="active-bike-img" />
                            )}
                        </div>

                        <div className="color-controls">
                            <h3>Цвет: {activeColor?.name}</h3>
                            <div className="color-dots">
                                {availableColors.map(color => (
                                    <div
                                        key={color.id}
                                        className={`color-dot ${activeColor?.id === color.id ? 'active' : ''}`}
                                        style={{ backgroundColor: color.hex }}
                                        onClick={() => setActiveColor(color)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* РАЗДЕЛИТЕЛЬ 1: ЛИНИИ И ФЛАГ ИЗ image_cb9a8a.jpg (МЕЖДУ ЦВЕТОМ И ХАРАКТЕРИСТИКАМИ) */}
            <div className="racing-divider white-bg">
                <div className="divider-line"></div>
                <img src="/assets/images/racing-flags.png" alt="Racing Flags" className="divider-flags" />
                <div className="divider-line"></div>
            </div>

            {/* 5. ТЕХНИЧЕСКИЕ ХАРАКТЕРИСТИКИ — ДИНАМИЧЕСКИЙ ВЫВОД ИЗ АДМИНКИ */}
            {model.specifications && Object.keys(model.specifications).length > 0 && (
                <section className="specs-section" ref={specificationsRef}>
                    <div className="specs-container">

                        <div className="specs-header-wrapper-simple">
                            <h2 className="specs-main-title">ТЕХНИЧЕСКИЕ ХАРАКТЕРИСТИКИ</h2>
                        </div>
                        <div className="specs-category-block">
                            <div className="specs-category-title">
                                <h3>Двигатель и <br />Ходовые качества</h3>
                            </div>
                            <div className="specs-table-wrapper">
                                <table className="specs-table">
                                    <tbody>
                                        {Object.entries(model.specifications).map(([key, value]) => (
                                            <tr key={key}>
                                                <td>{key}</td>
                                                <td>{value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* 6. РАЗДЕЛ КОМПЛЕКТАЦИИ — БЕЗОПАСНЫЙ ОТРЕНДЕРИНГ */}
            {model.equipment && Object.keys(model.equipment).length > 0 && (
                <section className="equipment-section">
                    <div className="container">
                        <h2 className="equipment-main-title">КОМПЛЕКТАЦИЯ МОДЕЛИ</h2>
                        <div className="equipment-grid-dynamic">
                            {Object.entries(model.equipment).map(([type, list]) => {
                                // Гарантируем получение массива элементов
                                let itemsArray = [];
                                if (Array.isArray(list)) {
                                    itemsArray = list;
                                } else if (typeof list === 'string') {
                                    itemsArray = list.split(',');
                                } else if (list) {
                                    itemsArray = [String(list)];
                                }
                                return (
                                    <div key={type} className="eq-block">
                                        <h3 className="eq-subtitle">{type}</h3>
                                        <ul className="eq-list">
                                            {itemsArray.map((item, i) => (
                                                <li key={i} className="eq-item">
                                                    {typeof item === 'string' ? item.trim() : String(item)}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* РАЗДЕЛИТЕЛЬ 2: МЕЖДУ КОМПЛЕКТАЦИЕЙ И ЧЕРНОЙ ФОТОГАЛЕРЕЕЙ */}
            <div className="racing-divider white-bg no-margin-bottom">
                <div className="divider-line"></div>
                <img src="/assets/images/racing-flags.png" alt="Racing Flags" className="divider-flags" />
                <div className="divider-line"></div>
            </div>

            {/* 4.1 МЕДИАГАЛЕРЕЯ (ФОТОГАЛЕРЕЯ) — ЧЕРНЫЙ ФОН ИЗ image_cb9056.png */}
            {model.gallery && model.gallery.length > 0 && (
                <section className="model-gallery-section">
                    <div className="gallery-container">
                        <h2 className="gallery-main-title">ФОТОГАЛЕРЕЯ</h2>
                        <div className="gallery-grid">
                            {model.gallery.map((item, index) => (
                                <div key={index} className="gallery-item">
                                    <img src={item.img} alt={item.alt || `BMW ${model.name}`} />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* РАЗДЕЛ: МОДЕЛИ И ПРАЙС-ЛИСТЫ (ПЕРЕНЕСЕННЫЕ БЛОКИ ФОТО И PDF) */}
            {model.price_models && model.price_models.length > 0 && (
                <section className="price-models-section">
                    <div className="container">
                        <div className="price-models-grid">
                            {model.price_models.map((item, index) => {
                                const pdfUrl = item.pdf_file ? `/storage/${item.pdf_file}` : null;
                                const imageUrl = item.block_image ? `/storage/${item.block_image}` : '';
                                return (
                                    <div key={index} className="price-model-card">
                                        {/* Большая буква серии */}
                                        <div className="price-model-series">{item.series}</div>

                                        {/* Картинка модели из отдельного блока */}
                                        <div className="price-model-image-wrapper">
                                            {imageUrl && (
                                                <img src={imageUrl} alt={item.name} />
                                            )}
                                        </div>

                                        {/* Название модели */}
                                        <h3 className="price-model-name">{item.name}</h3>

                                        {/* Кнопка загруженного PDF файла в самом конце */}
                                        <div className="price-model-action">
                                            {pdfUrl ? (
                                                <a href={pdfUrl} target="_blank" rel="noreferrer" className="price-btn active">
                                                    Скачать Прайс-лист
                                                </a>
                                            ) : (
                                                <button className="price-btn disabled" disabled>
                                                    Прайс отсутствует
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* 🔥 УМНЫЙ ВЫВОД ФОРМЫ ТЕСТ-ДРАЙВА */}
            {model.hasTestDrive !== false ? (
                <div id="test-drive-section">
                    <MotorradTestDrive defaultBike={model.name} />
                </div>
            ) : (
                <div className="test-drive-disabled-banner">
                    <p>Данная модель мотоцикла временно недоступна для тест-драйва в дилерском центре.</p>
                </div>
            )}

        </div>
    );
};

export default ModelDetail;